import { useState, useEffect, useCallback, ReactNode } from 'react'
import {
  Box, Typography, IconButton, Tooltip,
  CircularProgress, Chip, Collapse,
} from '@mui/material'
import CfpSelect from '../comps/CfpSelect'
import TabHeader from '../comps/TabHeader'
import RefreshIcon from '@mui/icons-material/Refresh'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import StopIcon from '@mui/icons-material/Stop'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import { useAppActions, useNav } from '../AppContext'
import { addToast } from '../services/Toasts'
import taapi from '../services/taapi'
import { fetchTasks, EMPTY_TKINIT, setTkInitListener, setTaskQueueListener, getQuestionProcessingSnapshot, sendTaskstreamMessage, type TaskQueueSnapshot } from '../services/TaskManagerService'
import { FS_XS, FS_MICRO, FS_SM, ICON_SM, ICON_MD } from '../common/theme'
import type { Task } from '../types'
import { DataTable, type DataTableColumn } from '../comps/DataTable'
import DebugJsonButton from '../comps/DebugJsonButton'
import { useQuestionProcessingVersion } from '../services/TaskManagerService'
import { DEBUG_MODE } from '../common/common'

const PAGE_SIZES = [20, 50, 100]

// We only ever surface the most recent N finished tasks. Anything beyond is
// hidden - the badge shows "N+" so the user knows there's more.
const FINISHED_VIEW_CAP = 100

type TaskSubTab = 'queue' | 'finished'

// ─── Paginator ────────────────────────────────────────────────────────────────

function Paginator({
  total, page, pageSize, onPage, onPageSize,
}: {
  total: number
  page: number
  pageSize: number
  onPage: (p: number) => void
  onPageSize: (s: number) => void
}) {
  const totalPages = Math.ceil(total / pageSize) || 1
  const safePage = Math.min(page, totalPages - 1)
  const start = safePage * pageSize + 1
  const end = Math.min((safePage + 1) * pageSize, total)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 1, py: 0.75, gap: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.6, whiteSpace: 'nowrap', mr: 0.5 }}>
        {total === 0 ? '0 tasks' : `Tasks ${start}–${end} of ${total}`}
      </Typography>
      <IconButton size="small" disabled={safePage === 0} onClick={() => onPage(0)} sx={{ p: 0.25 }}>
        <FirstPageIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <Typography variant="caption"
        onClick={safePage > 0 ? () => onPage(safePage - 1) : undefined}
        sx={{ fontSize: FS_SM, cursor: safePage > 0 ? 'pointer' : 'default', opacity: safePage > 0 ? 1 : 0.35, userSelect: 'none', px: 0.25 }}
      >Prev</Typography>
      {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i).map(pg => (
        <Box key={pg} onClick={() => onPage(pg)} sx={{
          minWidth: 22, height: 22, px: 0.25, borderRadius: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          bgcolor: safePage === pg ? 'primary.main' : 'transparent',
          border: safePage === pg ? 'none' : '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: safePage === pg ? 'primary.main' : 'action.hover' },
        }}>
          <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: safePage === pg ? 700 : 400, color: safePage === pg ? '#fff' : 'text.primary', userSelect: 'none' }}>
            {pg + 1}
          </Typography>
        </Box>
      ))}
      <Typography variant="caption"
        onClick={safePage < totalPages - 1 ? () => onPage(safePage + 1) : undefined}
        sx={{ fontSize: FS_SM, cursor: safePage < totalPages - 1 ? 'pointer' : 'default', opacity: safePage < totalPages - 1 ? 1 : 0.35, userSelect: 'none', px: 0.25 }}
      >Next</Typography>
      <IconButton size="small" disabled={safePage >= totalPages - 1} onClick={() => onPage(totalPages - 1)} sx={{ p: 0.25 }}>
        <LastPageIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <Box sx={{ ml: 0.5 }}>
        <CfpSelect<number>
          value={pageSize}
          onChange={(n) => { onPageSize(n); onPage(0) }}
          options={PAGE_SIZES.map(n => ({ value: n, label: `${n} / page` }))}
        />
      </Box>
    </Box>
  )
}

// ─── Count chip ───────────────────────────────────────────────────────────────

function CountChip({ count, color, overLimit }: {
  count: number
  color: 'success' | 'warning' | 'default'
  overLimit?: boolean
}) {
  if (count === 0) return null
  return (
    <Chip
      size="small"
      label={overLimit ? `${count}+` : count}
      color={color}
      sx={{ height: 16, fontSize: FS_MICRO, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
    />
  )
}

// ─── Section label ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ px: 1, py: 0.375, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
      <Typography variant="caption" sx={{ fontSize: FS_MICRO, fontWeight: 700, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {children}
      </Typography>
    </Box>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

function tasksSubTabFromUrl(): TaskSubTab {
  const parts = window.location.pathname.split('/').filter(Boolean)
  // ['app', 'tasks', subtab?]
  const sub = parts[2]
  return (sub === 'queue' || sub === 'finished') ? sub : 'queue'
}

export default function TaskManagerTab() {
  const { activeTab } = useNav()
  const { restartTaskstream } = useAppActions()
  const [tkInit, setTkInit] = useState(EMPTY_TKINIT)
  // Queue snapshot is pushed live by the server on every state change
  // (enqueue / start running / finish / cancel), separately from the
  // tkinit fetch which now serves finished tasks only.
  const [taskQueue, setTaskQueue] = useState<TaskQueueSnapshot>({ tasks: [], countrunning: 0, countwaiting: 0 })
  const [loading, setLoading] = useState(false)
  const [subTab, setSubTab] = useState<TaskSubTab>(() => tasksSubTabFromUrl())
  const [expandedShas, setExpandedShas] = useState<Set<string>>(new Set())

  // Mirror the active sub-tab into the URL so refresh/back/forward land on the
  // same tab. Only push when this top-level tab is the visible one - otherwise
  // we'd clobber another tab's URL.
  useEffect(() => {
    if (activeTab !== 'tasks') return
    const target = `/app/tasks/${subTab}`
    if (window.location.pathname !== target) {
      window.history.pushState({}, '', target)
    }
  }, [subTab, activeTab])

  // Re-derive the sub-tab from the URL on back/forward.
  useEffect(() => {
    const onPop = () => setSubTab(tasksSubTabFromUrl())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  useEffect(() => {
    return setTkInitListener(setTkInit)
  }, [])

  // Live queue subscription. Fires immediately with the last-known
  // snapshot (or the empty default) and then on every taskqueue push.
  useEffect(() => {
    return setTaskQueueListener(setTaskQueue)
  }, [])

  const refreshTasks = useCallback(async () => {
    const result = await fetchTasks()
    if (result) setTkInit(result)
  }, [])
  const [queuedPage, setQueuedPage] = useState(0)
  const [queuedPageSize, setQueuedPageSize] = useState(20)
  const [finishedPage, setFinishedPage] = useState(0)
  const [finishedPageSize, setFinishedPageSize] = useState(20)
  // Subscribe to streaming-store changes so the debug snapshot re-renders
  // live while the dialog is open.
  useQuestionProcessingVersion()

  useEffect(() => {
    if (activeTab === 'tasks') refreshTasks()
  }, [activeTab])

  const handleRefresh = useCallback(async () => {
    setLoading(true)
    try { await refreshTasks() }
    finally { setLoading(false) }
  }, [refreshTasks])

  const handleStopAll = useCallback(async () => {
    // Prefer the live taskstream WebSocket control channel; fall back to the
    // REST endpoint when we're on the SSE transport (no client→server channel).
    if (sendTaskstreamMessage({ type: 'cancelall' })) {
      addToast('success', 'Stopped all tasks')
      refreshTasks()
      return
    }
    try {
      const result = await taapi.get('/api2/tkstopall', {})
      if (result?.success) {
        addToast('success', 'Stopped all tasks')
        refreshTasks()
      } else {
        addToast('error', 'Error stopping all tasks')
      }
    } catch {
      addToast('error', 'Error stopping all tasks')
    }
  }, [refreshTasks, addToast])

  const handleStop = useCallback(async (tasksha: string) => {
    // Prefer the live taskstream WebSocket control channel; fall back to REST.
    if (sendTaskstreamMessage({ type: 'cancel', tasksha })) {
      addToast('success', 'Task stopped')
      refreshTasks()
      return
    }
    try {
      const result = await taapi.get('/api2/tkstop', { tasksha })
      if (result?.success) {
        addToast('success', 'Task stopped')
        refreshTasks()
      } else {
        addToast('error', 'Error stopping task')
      }
    } catch {
      addToast('error', 'Error stopping task')
    }
  }, [addToast, refreshTasks])

  const toggleExpand = (tasksha: string) => {
    setExpandedShas(prev => {
      const next = new Set(prev)
      if (next.has(tasksha)) next.delete(tasksha); else next.add(tasksha)
      return next
    })
  }

  // Queue is push-driven (taskqueue WS event); finished is poll-driven
  // (tkinit response, which now returns finished-only tasks).
  const runningTasks = taskQueue.tasks.filter(t => t.isrunning)
  const waitingTasks = taskQueue.tasks.filter(t => t.iswaiting)
  // Cap the finished list at FINISHED_VIEW_CAP - older runs aren't shown.
  // The badge gets a "+" suffix when there are more behind the cap.
  const finishedAll = tkInit.tasks.filter(t => t.isfinished)
  const finishedHasMore = finishedAll.length >= FINISHED_VIEW_CAP
  const finishedTasks = finishedAll.slice(0, FINISHED_VIEW_CAP)

  const safeQueuedPage = Math.min(queuedPage, Math.max(0, Math.ceil(waitingTasks.length / queuedPageSize) - 1))
  const pagedWaiting = waitingTasks.slice(safeQueuedPage * queuedPageSize, (safeQueuedPage + 1) * queuedPageSize)

  const safeFinishedPage = Math.min(finishedPage, Math.max(0, Math.ceil(finishedTasks.length / finishedPageSize) - 1))
  const pagedFinished = finishedTasks.slice(safeFinishedPage * finishedPageSize, (safeFinishedPage + 1) * finishedPageSize)

  const taskCols: DataTableColumn<Task>[] = [
    {
      id: 'status', iconColumn: true, width: 32,
      render: (t) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          {t.errorflag
            ? <ErrorOutlineIcon sx={{ fontSize: ICON_SM, color: 'error.main' }} />
            : t.isrunning
              ? <CircularProgress size={13} thickness={5} />
              : t.iswaiting
                ? <AccessTimeIcon sx={{ fontSize: ICON_SM, color: 'warning.main', opacity: 0.7 }} />
                : <TaskAltIcon sx={{ fontSize: ICON_SM, color: 'success.main' }} />}
        </Box>
      ),
    },
    {
      id: 'type', label: 'Type', flex: 1, width: 0, minWidth: 80,
      render: (t) => (
        <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
          {t.tasktypename}
        </Typography>
      ),
    },
    {
      id: 'message', label: 'Message', flex: 2, width: 0, minWidth: 100,
      render: (t) => (
        <Typography variant="caption" sx={{
          fontSize: FS_XS,
          color: t.errorflag ? 'error.main' : 'text.secondary',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
        }}>
          {(t.errorflag && t.errormsg) ? t.errormsg : (t.msglist?.[0] ?? '-')}
        </Typography>
      ),
    },
    {
      id: 'duration', label: 'Duration', width: 80,
      render: (t) => <Typography variant="caption" sx={{ fontSize: '0.68rem', opacity: 0.5 }}>{t.durationstring}</Typography>,
    },
    {
      id: 'updated', label: 'Updated', width: 140,
      render: (t) => <Typography variant="caption" sx={{ fontSize: '0.68rem', opacity: 0.5 }}>{t.updateddate}</Typography>,
    },
    {
      id: 'actions', iconColumn: true, width: 56,
      render: (t) => {
        const canStop = t.state !== 'end'
        const hasMore = (t.msglist?.length ?? 0) > 1
        const isExpanded = expandedShas.has((t as any).tasksha)
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {canStop && (
              <Tooltip title="Stop task">
                <IconButton data-testid="tm_task_stop_btn" size="small"
                  onClick={(e) => { e.stopPropagation(); handleStop((t as any).tasksha) }}
                  sx={{ p: 0.25 }} color="error">
                  <StopIcon sx={{ fontSize: ICON_SM }} />
                </IconButton>
              </Tooltip>
            )}
            {hasMore && (
              <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                <IconButton size="small"
                  onClick={(e) => { e.stopPropagation(); toggleExpand((t as any).tasksha) }}
                  sx={{ p: 0.25 }}>
                  {isExpanded
                    ? <ExpandLessIcon sx={{ fontSize: ICON_SM, opacity: 0.5 }} />
                    : <ExpandMoreIcon sx={{ fontSize: ICON_SM, opacity: 0.5 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )
      },
    },
  ]

  const renderRowExtra = (t: Task) => {
    if ((t.msglist?.length ?? 0) <= 1) return null
    return (
      <Collapse in={expandedShas.has((t as any).tasksha)}>
        <Box sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
          {t.msglist?.slice(1).map((m, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block', opacity: 0.65, fontSize: FS_XS, py: 0.1 }}>
              {m}
            </Typography>
          ))}
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, opacity: 0.45 }}>
            <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>Started: {t.startdate}</Typography>
          </Box>
        </Box>
      </Collapse>
    )
  }

  const renderTaskTable = (rows: Task[], emptyText: string) => (
    <DataTable<Task>
      rows={rows}
      cols={taskCols}
      rowKey={(t) => (t as any).tasksha}
      rowTestId="tm_task_row"
      rowDataAttrs={(t) => ({ 'data-tasksha': (t as any).tasksha, 'data-state': t.state })}
      rowExtra={renderRowExtra}
      emptyContent={
        <Box sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ opacity: 0.4 }}>{emptyText}</Typography>
        </Box>
      }
    />
  )

  return (
    <Box data-testid="tm_root" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Task Queue</Typography>
        {DEBUG_MODE && (
          <>
            <DebugJsonButton
              value={getQuestionProcessingSnapshot()}
              title="Question streaming snapshot"
              tooltip="Inspect question streaming map (debug)"
              testIdPrefix="tm_streaming_debug"
            />
            <Tooltip title="Restart taskstream (debug) - aborts and reconnects the SSE socket">
              <IconButton
                size="small"
                data-testid="tm_restart_taskstream_btn"
                onClick={restartTaskstream}
                sx={{ p: 0.25 }}
              >
                <RestartAltIcon sx={{ fontSize: ICON_SM, opacity: 0.55 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        {(taskQueue.countrunning > 0 || taskQueue.countwaiting > 0) && (
          <Tooltip title="Stop All Tasks">
            <IconButton data-testid="tm_stopall_btn" size="small" onClick={handleStopAll} color="error">
              <StopCircleOutlinedIcon sx={{ fontSize: ICON_MD }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Refresh">
          <IconButton data-testid="tm_refresh_btn" size="small" onClick={handleRefresh} disabled={loading}>
            {loading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: ICON_MD }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tab strip */}
      <TabHeader<TaskSubTab>
        tabs={[
          { key: 'queue', label: 'Queue', trailing: <CountChip count={taskQueue.countrunning + taskQueue.countwaiting} color={'success'} /> },
          { key: 'finished', label: 'Finished', trailing: <CountChip count={finishedTasks.length} color="default" overLimit={finishedHasMore} /> },
        ]}
        active={subTab}
        onChange={setSubTab}
        testIdPrefix="taskmanager"
      />

      {/* Queue tab - running at top, queued below (paginator above each table).
          Counts come straight from `taskQueue.countrunning` / `countwaiting`
          which the server computes via a dedicated count query, so they
          stay accurate even when the tasks list is capped at 100. */}
      {subTab === 'queue' && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          <Box sx={{ flex: '0 0 auto', maxHeight: '50%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SectionLabel>Running ({taskQueue.countrunning})</SectionLabel>
            {renderTaskTable(runningTasks, 'No running tasks')}
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderTop: 2, borderColor: 'divider' }}>

            <Paginator
              total={taskQueue.countwaiting}
              page={safeQueuedPage}
              pageSize={queuedPageSize}
              onPage={setQueuedPage}
              onPageSize={setQueuedPageSize}
            />
            <SectionLabel>Queued ({taskQueue.countwaiting})</SectionLabel>
            {renderTaskTable(pagedWaiting, 'No queued tasks')}
          </Box>
        </Box>
      )}

      {/* Finished tab */}
      {subTab === 'finished' && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Paginator
            total={finishedTasks.length}
            page={safeFinishedPage}
            pageSize={finishedPageSize}
            onPage={setFinishedPage}
            onPageSize={setFinishedPageSize}
          />
          {renderTaskTable(pagedFinished, 'No finished tasks')}
        </Box>
      )}

    </Box>
  )
}
